import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRouteHelpers } from "routes/helpers";
import routes from "routes/routes";

import { COMPANIES_LIST_FETCH_ALL_LIMIT } from "constants/company/companiesList";
import { showErrorModal } from "helpers";
import useAuth from "hooks/useAuth";
import { useCompaniesList } from "hooks/useCompaniesList";

import { ListUserCompaniesResDto } from "utils/swagger_react_query";

import { ISignUpPageLocationState } from "../SignUpPage/types";

export const useSelectCompanyPage = () => {
  const navigate = useNavigate();
  const { redirectByRoleAndSignUpStatus } = useRouteHelpers();

  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isListLoading, setIsListLoading] = useState(false);
  const { companiesList, searchInputValue, actions } = useCompaniesList({
    setIsPageLoading,
    setIsListLoading,
    fetchOnMount: true,
  });
  const { handleSearchInputChange, handlePageChange } = actions;

  const { selectActiveCompany, getCurrentUserAndCompany } = useAuth();

  const handleCompanyClick = async (companyId: string) => {
    try {
      const shouldRefetchList = companiesList.companies.find(
        (it) => it.companyId === companyId && !it.hasSignedIn,
      );
      let companiesListRes: ListUserCompaniesResDto | undefined = companiesList;

      setIsListLoading(true);
      await selectActiveCompany(companyId);
      const userAndCompanyDataRes = await getCurrentUserAndCompany();
      if (shouldRefetchList)
        companiesListRes = await actions.refetchCompanies(
          {
            page: "1",
            perPage: COMPANIES_LIST_FETCH_ALL_LIMIT,
          },
          true,
        );
      const companyIds = companiesListRes?.companies?.map((it) => it.companyId) || [];
      redirectByRoleAndSignUpStatus(userAndCompanyDataRes, companyIds);
    } catch (error) {
      showErrorModal(error);
    } finally {
      setIsListLoading(false);
    }
  };

  const handleCreateNewCompanyClick = () => {
    const locationState: ISignUpPageLocationState = {
      isAdditionalCompanyCreation: true,
    };
    navigate(routes.SIGN_UP_COMPLETE, { state: locationState });
  };

  return {
    metadata: {
      isPageLoading,
      isListLoading,
    },
    pageData: {
      companiesList,
      searchInputValue,
    },
    actions: {
      handleSearchInputChange,
      handlePageChange,
      handleCompanyClick,
      handleCreateNewCompanyClick,
    },
  };
};
